import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchOrganization,
  updateOrganization,
  grantCredits,
  debitCredits,
  fetchOrgSkills,
  enableSkill,
  disableSkill,
  cancelSubscription,
} from '@api/backoffice';
import useSnackbar from '@hooks/useSnackbar';
import { useTableSort } from '@hooks/useTableSort';
import ThSortable from '@components/Backoffice/ThSortable';
import ExportButton from '@components/Backoffice/ExportButton';
import { exportArrayToCsv } from '@utils/csvExport';
import type { OrgDetailData, SkillResponse, EditOrgDialogProps, ManageCreditsDialogProps } from './types';
import {
  PageTitle,
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  ActionButton,
  CardHeaderLabel,
  TabBar,
  Tab,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  FormField,
  FormLabel,
  FormInput,
  ButtonRow,
  WarningBanner,
  ButtonGroup,
  EmptyState,
  ToggleButton,
  SmallText,
  TruncatedCell,
} from '@components/Backoffice/BackofficeLayout.styled';

const BackofficeOrgDetail: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const [org, setOrg] = useState<OrgDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showManageCredits, setShowManageCredits] = useState(false);
  const [showCancelSub, setShowCancelSub] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [activeTab, setActiveTab] = useState<'members' | 'skills'>('members');
  const [skills, setSkills] = useState<SkillResponse[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillsLoaded, setSkillsLoaded] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState<SkillResponse | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await fetchOrganization(orgId);
      setOrg(data);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const loadSkills = useCallback(async () => {
    if (!orgId) return;
    setSkillsLoading(true);
    try {
      const data = await fetchOrgSkills(orgId);
      setSkills(data);
      setSkillsLoaded(true);
    } finally {
      setSkillsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (activeTab === 'skills' && !skillsLoaded) {
      loadSkills();
    }
  }, [activeTab, skillsLoaded, loadSkills]);

  const handleCancelSubscription = async () => {
    if (!orgId) return;
    setCancelling(true);
    try {
      await cancelSubscription(orgId);
      showSuccess('Subscription cancelled successfully');
      setShowCancelSub(false);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleToggleSkill = async (skill: SkillResponse) => {
    if (!orgId) return;
    try {
      if (skill.enabled) {
        await disableSkill(orgId, skill.skillId);
      } else {
        await enableSkill(orgId, skill.skillId);
      }
      showSuccess(`${skill.displayName} ${skill.enabled ? 'disabled' : 'enabled'}`);
      setConfirmToggle(null);
      await loadSkills();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update skill');
      setConfirmToggle(null);
    }
  };

  const {
    sorted: sortedMembers,
    sortKey: memberSortKey,
    sortDirection: memberSortDir,
    toggle: memberToggle,
  } = useTableSort(org?.members ?? [], 'email');

  const {
    sorted: sortedSkills,
    sortKey: skillSortKey,
    sortDirection: skillSortDir,
    toggle: skillToggle,
  } = useTableSort(skills, 'displayName');

  if (loading) return <EmptyState>Loading...</EmptyState>;
  if (!org) return <EmptyState>Organization not found</EmptyState>;

  const memberTh = (label: string, key: string) => (
    <ThSortable
      label={label}
      sortKey={key}
      activeKey={memberSortKey}
      direction={memberSortDir}
      onToggle={memberToggle}
    />
  );

  const skillTh = (label: string, key: string) => (
    <ThSortable
      label={label}
      sortKey={key}
      activeKey={skillSortKey}
      direction={skillSortDir}
      onToggle={skillToggle}
    />
  );

  return (
    <>
      <PageHeader>
        <ActionButton $variant="ghost" onClick={() => navigate('/backoffice-admin/organizations')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </ActionButton>
        <PageTitle>{org.name}</PageTitle>
        <Badge $variant={org.billingStatus === 'ACTIVE' ? 'success' : 'warning'}>
          {org.billingStatus}
        </Badge>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardHeaderLabel>Details</CardHeaderLabel>
          <ButtonGroup>
            {org.stripeSubscriptionId && (
              <ActionButton $variant="danger" onClick={() => setShowCancelSub(true)}>
                Cancel Subscription
              </ActionButton>
            )}
            <ActionButton $variant="primary" onClick={() => setShowManageCredits(true)}>
              Manage Credits
            </ActionButton>
            <ActionButton $variant="primary" onClick={() => setShowEdit(true)}>
              Edit
            </ActionButton>
          </ButtonGroup>
        </CardHeader>
        <CardBody>
          <DetailGrid>
            <DetailItem>
              <DetailLabel>Org ID</DetailLabel>
              <DetailValue><SmallText>{org.id}</SmallText></DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Plan</DetailLabel>
              <DetailValue>{org.planType || '—'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Tier</DetailLabel>
              <DetailValue>{org.planTierId || '—'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Billing Period</DetailLabel>
              <DetailValue>{org.billingPeriod || '—'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Monthly Credits</DetailLabel>
              <DetailValue>{org.monthlyCreditAllowance}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Credit Balance</DetailLabel>
              <DetailValue>{org.creditBalance}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>MFA Enforced</DetailLabel>
              <DetailValue>{org.mfaEnforced ? 'Yes' : 'No'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Created</DetailLabel>
              <DetailValue>{new Date(org.createdAt).toLocaleString()}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Stripe Customer</DetailLabel>
              <DetailValue>{org.stripeCustomerId || '—'}</DetailValue>
            </DetailItem>
          </DetailGrid>
        </CardBody>
      </Card>

      <TabBar>
        <Tab $active={activeTab === 'members'} onClick={() => setActiveTab('members')}>
          Members ({org.members.length})
        </Tab>
        <Tab $active={activeTab === 'skills'} onClick={() => setActiveTab('skills')}>
          Skills
        </Tab>
      </TabBar>

      {activeTab === 'members' && (
        <>
          <Card>
            <CardHeader>
              <span />
              <ExportButton
                onExport={async () =>
                  exportArrayToCsv(
                    sortedMembers,
                    [
                      { header: 'Email', accessor: (m) => m.email },
                      { header: 'Name', accessor: (m) => m.displayName || '' },
                      { header: 'Role', accessor: (m) => m.orgRole },
                      { header: 'Expiry', accessor: (m) => m.expiryDate ? new Date(m.expiryDate).toISOString() : '' },
                    ],
                    `org_members_${orgId}_${new Date().toISOString().slice(0, 10)}.csv`
                  )
                }
              />
            </CardHeader>
            <Table>
              <thead>
                <tr>
                  {memberTh('Email', 'email')}
                  {memberTh('Name', 'displayName')}
                  {memberTh('Role', 'orgRole')}
                  {memberTh('Expires', 'expiryDate')}
                </tr>
              </thead>
              <tbody>
                {sortedMembers.map((m) => (
                  <tr
                    key={m.id}
                    onClick={() => navigate(`/backoffice-admin/users/${m.id}`)}
                  >
                    <td>{m.email}</td>
                    <td>{m.displayName || '—'}</td>
                    <td>
                      <Badge
                        $variant={
                          m.orgRole === 'AIBUILD_ADMIN'
                            ? 'info'
                            : m.orgRole === 'ORG_ADMIN'
                              ? 'success'
                              : 'warning'
                        }
                      >
                        {m.orgRole}
                      </Badge>
                    </td>
                    <td>{m.expiryDate ? new Date(m.expiryDate).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      {activeTab === 'skills' && (
        <Card>
          {skillsLoading ? (
            <EmptyState>Loading skills...</EmptyState>
          ) : skills.length === 0 ? (
            <EmptyState>No skills defined</EmptyState>
          ) : (
            <>
            <CardHeader>
              <span />
              <ExportButton
                onExport={async () =>
                  exportArrayToCsv(
                    sortedSkills,
                    [
                      { header: 'Skill', accessor: (s) => s.displayName },
                      { header: 'Description', accessor: (s) => s.description || '' },
                      { header: 'Type', accessor: (s) => s.acquisitionType || '' },
                      { header: 'Status', accessor: (s) => s.status || '' },
                      { header: 'Enabled', accessor: (s) => String(s.enabled) },
                      { header: 'Expires', accessor: (s) => s.expiresAt ? new Date(s.expiresAt).toISOString() : '' },
                    ],
                    `org_skills_${orgId}_${new Date().toISOString().slice(0, 10)}.csv`
                  )
                }
              />
            </CardHeader>
            <Table>
              <thead>
                <tr>
                  {skillTh('Skill', 'displayName')}
                  {skillTh('Description', 'description')}
                  {skillTh('Type', 'acquisitionType')}
                  {skillTh('Status', 'status')}
                  {skillTh('Expires', 'expiresAt')}
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {sortedSkills.map((skill) => (
                  <tr key={skill.skillId}>
                    <td><strong>{skill.displayName}</strong></td>
                    <TruncatedCell>{skill.description}</TruncatedCell>
                    <td>
                      <Badge
                        $variant={skill.acquisitionType === 'INCLUDED' ? 'info' : 'warning'}
                      >
                        {skill.acquisitionType}
                      </Badge>
                    </td>
                    <td>{skill.status || '—'}</td>
                    <td>
                      {skill.expiresAt
                        ? new Date(skill.expiresAt).toLocaleDateString()
                        : '—'}
                    </td>
                    <td>
                      {skill.acquisitionType !== 'INCLUDED' ? (
                        <ToggleButton
                          $active={skill.enabled}
                          onClick={() => setConfirmToggle(skill)}
                        />
                      ) : (
                        <Badge $variant="success">Always On</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </>
          )}
        </Card>
      )}

      {showEdit && (
        <EditOrgDialog
          org={org}
          onClose={() => setShowEdit(false)}
          onSave={() => {
            showSuccess('Organization updated');
            load();
          }}
          onError={(msg) => showError(msg)}
        />
      )}
      {showManageCredits && (
        <ManageCreditsDialog
          orgId={org.id}
          orgName={org.name}
          creditBalance={org.creditBalance}
          onClose={() => setShowManageCredits(false)}
          onSave={(msg) => {
            showSuccess(msg);
            load();
          }}
          onError={(msg) => showError(msg)}
        />
      )}
      {showCancelSub && (
        <DialogOverlay onClick={() => setShowCancelSub(false)}>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <WarningBanner>
              This will immediately cancel the Stripe subscription for{' '}
              <strong>{org.name}</strong>. The organization will be suspended and lose
              access. Stripe will handle any prorated refund for the remaining period.
              This action cannot be undone.
            </WarningBanner>
            <CardBody>
              <p>
                <strong>Current plan:</strong> {org.planTierId || '—'}<br />
                <strong>Stripe subscription:</strong> {org.stripeSubscriptionId}
              </p>
            </CardBody>
            <ButtonRow>
              <ActionButton $variant="ghost" onClick={() => setShowCancelSub(false)}>
                Keep Subscription
              </ActionButton>
              <ActionButton
                $variant="danger"
                onClick={handleCancelSubscription}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Subscription Now'}
              </ActionButton>
            </ButtonRow>
          </DialogContent>
        </DialogOverlay>
      )}
      {confirmToggle && (
        <DialogOverlay onClick={() => setConfirmToggle(null)}>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <DialogTitle>
              {confirmToggle.enabled ? 'Disable' : 'Enable'} {confirmToggle.displayName}?
            </DialogTitle>
            <CardBody>
              Are you sure you want to {confirmToggle.enabled ? 'disable' : 'enable'}{' '}
              <strong>{confirmToggle.displayName}</strong> for this organization?
            </CardBody>
            <ButtonRow>
              <ActionButton $variant="ghost" onClick={() => setConfirmToggle(null)}>
                Cancel
              </ActionButton>
              <ActionButton
                $variant={confirmToggle.enabled ? 'danger' : 'primary'}
                onClick={() => handleToggleSkill(confirmToggle)}
              >
                {confirmToggle.enabled ? 'Disable' : 'Enable'}
              </ActionButton>
            </ButtonRow>
          </DialogContent>
        </DialogOverlay>
      )}
    </>
  );
};

function EditOrgDialog({ org, onClose, onSave, onError }: EditOrgDialogProps) {
  const [mfaEnforced, setMfaEnforced] = useState(org.mfaEnforced);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateOrganization(org.id, { mfaEnforced });
      onSave();
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Edit Organization</DialogTitle>
        <FormField>
          <FormLabel>Billing Status</FormLabel>
          <FormInput value={org.billingStatus || '—'} disabled />
        </FormField>
        <FormField>
          <FormLabel>Plan</FormLabel>
          <FormInput value={org.planTierId || '—'} disabled />
        </FormField>
        <FormField>
          <FormLabel>Monthly Credit Allowance</FormLabel>
          <FormInput type="number" value={org.monthlyCreditAllowance} disabled />
        </FormField>
        <FormField>
          <FormLabel>MFA Enforced</FormLabel>
          <ToggleButton
            $active={mfaEnforced}
            onClick={() => setMfaEnforced((v) => !v)}
            type="button"
          />
        </FormField>
        <ButtonRow>
          <ActionButton $variant="ghost" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton $variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </ActionButton>
        </ButtonRow>
      </DialogContent>
    </DialogOverlay>
  );
}

const MAX_GRANT_AMOUNT = 1000;

function ManageCreditsDialog({
  orgId,
  orgName,
  creditBalance,
  onClose,
  onSave,
  onError,
}: ManageCreditsDialogProps) {
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const [amount, setAmount] = useState('');
  const [expiryMonths, setExpiryMonths] = useState('12');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const numAmount = Number(amount) || 0;
  const maxAmount = mode === 'add' ? MAX_GRANT_AMOUNT : creditBalance;
  const isOverMax = numAmount > maxAmount;
  const isValid = numAmount > 0 && !isOverMax;

  const handleModeChange = (newMode: 'add' | 'remove') => {
    setMode(newMode);
    setAmount('');
  };

  const handleAmountChange = (value: string) => {
    const num = Number(value);
    if (value === '' || (num >= 0 && num <= maxAmount)) {
      setAmount(value);
    }
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      if (mode === 'add') {
        await grantCredits({
          organizationId: orgId,
          amount: numAmount,
          expiryMonths: Number(expiryMonths) || 12,
        });
        onSave(`Granted ${numAmount} credits to ${orgName}`);
      } else {
        await debitCredits({
          organizationId: orgId,
          amount: numAmount,
          reason: reason.trim() || undefined,
        });
        onSave(`Removed ${numAmount} credits from ${orgName}`);
      }
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : `Failed to ${mode} credits`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Manage Credits — {orgName}</DialogTitle>

        <TabBar>
          <Tab $active={mode === 'add'} onClick={() => handleModeChange('add')}>
            Add Credits
          </Tab>
          <Tab $active={mode === 'remove'} onClick={() => handleModeChange('remove')}>
            Remove Credits
          </Tab>
        </TabBar>

        <FormField>
          <FormLabel>
            Amount {mode === 'add' ? `(max ${MAX_GRANT_AMOUNT})` : `(max ${creditBalance} available)`}
          </FormLabel>
          <FormInput
            type="number"
            placeholder={mode === 'add' ? 'e.g. 100' : `Up to ${creditBalance}`}
            value={amount}
            min={0}
            max={maxAmount}
            onChange={(e) => handleAmountChange(e.target.value)}
            autoFocus
          />
        </FormField>

        {mode === 'add' && (
          <FormField>
            <FormLabel>Expiry (months)</FormLabel>
            <FormInput
              type="number"
              value={expiryMonths}
              min={1}
              onChange={(e) => setExpiryMonths(e.target.value)}
            />
          </FormField>
        )}

        {mode === 'remove' && (
          <FormField>
            <FormLabel>Reason (optional)</FormLabel>
            <FormInput
              value={reason}
              placeholder="e.g. Billing adjustment"
              onChange={(e) => setReason(e.target.value)}
            />
          </FormField>
        )}

        {mode === 'remove' && isValid && (
          <WarningBanner>
            This will immediately remove {numAmount} credits from this organization.
            Balance after: {creditBalance - numAmount}.
          </WarningBanner>
        )}

        <ButtonRow>
          <ActionButton $variant="ghost" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton
            $variant={mode === 'add' ? 'primary' : 'danger'}
            onClick={handleSubmit}
            disabled={saving || !isValid}
          >
            {saving
              ? (mode === 'add' ? 'Granting...' : 'Removing...')
              : (mode === 'add' ? 'Grant Credits' : 'Remove Credits')}
          </ActionButton>
        </ButtonRow>
      </DialogContent>
    </DialogOverlay>
  );
}

export default BackofficeOrgDetail;
